// Reflowing vertical stack for the bottom-centre HUD elements (stamina bar, block bar, interaction
// prompt). They used to sit at fixed `bottom:` offsets and overlap / leave gaps as each showed and
// hid independently. Here they share one anchored stack: the HIGHEST-priority slot sits at the base
// and never moves; lower-priority slots stack ABOVE it, and only count the higher slots that are
// actually visible — so there are never gaps or overlaps, and a slot's position changes based on what
// else is showing. Priority (highest first): stamina → block → prompt.

export type BottomSlot = "stamina" | "block" | "prompt";

const ORDER: BottomSlot[] = ["stamina", "block", "prompt"]; // highest priority first (base → up)
const BASE = 92; // px from the viewport bottom — anchor of the stack (above the chat/toast row at 84)
const GAP = 8;   // px between stacked elements

const els: Partial<Record<BottomSlot, HTMLElement>> = {};
const shown: Record<BottomSlot, boolean> = { stamina: false, block: false, prompt: false };

function relayout(): void {
  let offset = BASE;
  for (const key of ORDER) {
    const el = els[key];
    if (!el || !shown[key]) continue; // hidden slots take no space (lower ones drop to fill)
    el.style.bottom = `${offset}px`;
    offset += el.offsetHeight + GAP; // measured live so the prompt's variable height is respected
  }
}

/** Register a slot's element. Call once when the element is mounted. */
export function registerBottomSlot(key: BottomSlot, el: HTMLElement): void {
  els[key] = el;
  relayout();
}

/** Show/hide a slot in the stack (drives the reflow). Safe to call every frame — it no-ops unless the
 *  visibility actually changed. Pass `force` to relayout even when unchanged (e.g. prompt text resized). */
export function setBottomSlot(key: BottomSlot, visible: boolean, force = false): void {
  if (shown[key] === visible && !force) return;
  shown[key] = visible;
  relayout();
}
